import React, { FC, useEffect, useState } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import styled from 'styled-components';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import WarningIcon from '@mui/icons-material/Warning';
import {
  getUserDisplayName,
  getUserStatusTone,
} from '../../common/utils/services/user-list-utils';
import { useTranslation } from 'react-i18next';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import { Alert, AlertTitle, Tab, Tabs } from '@mui/material';
import { UserDetailTabType } from '../../common/model/type/user-detail-tab.type';
import { camelCase, last } from 'lodash';
import OvUserInfo from '../UI/organisms/OvUserInfo';
import OvUserInfoCalendar from '../UI/organisms/OvUserInfoCalendar';
import OvUserDetailReports from '../UI/organisms/OvUserDetailReports';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import OvInfoModal from '../UI/molecules/OvInfoModal';
import OvConfirmationInput from '../UI/molecules/OvConfirmationInput';
import InfoIcon from '@mui/icons-material/Info';
import OvResetPasswordDialog from '../UI/molecules/OvResetPasswordDialog';
import OvUserCharts, { DailyDataRange } from '../UI/organisms/OvUserCharts';
import { DailyDataService } from '../../services/daily-data.service';
import queryString from 'query-string';
import { endOfDay, startOfDay } from 'date-fns';
import OvSetPasswordDialog from '../UI/molecules/OvSetPasswordDialog';
import OvViewPasswordDialog from '../UI/molecules/OvViewPasswordDialog';
import { SetDemoAccountPasswordRequest } from '../../common/model/dto/set-demo-account-password-request.ts';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import OvUserSpecificBaselines from '../UI/organisms/OvUserSpecificBaselines';
import OvUserAuditActivity from '../UI/organisms/OvUserAuditActivity';
import UserScanList from './UserScanList';
import AdminNotificationList from './AdminNotificationList';
import OvUserScanningPlans from '../UI/organisms/OvUserScanningPlans';
import UserService from '../../services/user.service';
import { useQuery } from '@tanstack/react-query';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvUserSubscriptions from '../UI/organisms/OvUserSubscriptions';
import { useAdminUserMutations } from '../../hooks/use-admin-user-mutations';
import OvUserHormonePattern from '../UI/organisms/OvUserHormonePattern';
import OvReproductiveStageDetails from '../UI/organisms/OvReproductiveStageDetails';
import OvUserCycles from '../UI/organisms/OvUserCycles';
import OvOvulatoryEventPrediction from '../UI/organisms/OvOvulatoryEventPrediction';
import {
  getUserCycleHormoneSummary,
  getUserCycles,
} from '../../services/admin/admin-user-cycles.service';

const USER_LIST_PATH = '/users';

const UserDetails: FC = () => {
  let { userId }: any = useParams();
  const url = useRouteUrl();
  let location = useLocation();
  const [userListPath] = useState<string>(
    (location.state as { prevPath?: string } | null)?.prevPath ?? USER_LIST_PATH
  );
  const { t } = useTranslation();

  const applicationOperations = useAuthorized(Resource.Application);
  const userOperations = useAuthorized(Resource.User);
  const scanOperations = useAuthorized(Resource.Scan);
  const pdfOperations = useAuthorized(Resource.PdfReport);
  const userAuditLogActivityOperations = useAuthorized(
    Resource.UserAuditLogActivity
  );
  const userCycleOperations = useAuthorized(Resource.UserCycle);
  const hormonePatternOperations = useAuthorized(Resource.HormonePattern);
  const strawStageOperations = useAuthorized(Resource.StrawStage);
  const ovulatoryEventPredictionOperations = useAuthorized(
    Resource.OvulatoryEventPrediction
  );

  const userUpdateAllowed =
    userOperations.update || applicationOperations.supervise;
  const userDeleteAllowed =
    userOperations.delete || applicationOperations.supervise;
  const userAuditLogActivityReadAllowed =
    userAuditLogActivityOperations.read || applicationOperations.supervise;
  const scanReadAllowed =
    scanOperations.read || applicationOperations.supervise;
  const pdfReportsReadAllowed =
    pdfOperations.read || applicationOperations.supervise;
  const userCycleReadAllowed =
    userCycleOperations.read || applicationOperations.supervise;
  const hormonePatternReadAllowed =
    hormonePatternOperations.read || applicationOperations.supervise;
  const strawStageReadAllowed =
    strawStageOperations.read || applicationOperations.supervise;
  const ovulatoryEventPredictionReadAllowed =
    ovulatoryEventPredictionOperations.read || applicationOperations.supervise;

  const [activeTab, setActiveTab] = useState<UserDetailTabType | null>(null);

  const [
    isOpenDeactivateConfirmationDialog,
    setOpenDeactivateConfirmationDialog,
  ] = useState<boolean>(false);
  const [isOpenConfirmationInputModal, setOpenConfirmationInputModal] =
    useState<boolean>(false);
  const [isOpenDeleteUserDialog, setIsOpenDeleteUserDialog] =
    useState<boolean>(false);
  const [isResetPasswordDialogOpen, setIsResetPasswordDialogOpen] =
    useState(false);
  const [isCreatePasswordDialogOpen, setIsCreatePasswordDialogOpen] =
    useState(false);
  const [isViewPasswordDialogOpen, setIsViewPasswordDialogOpen] =
    useState(false);
  const [isDeleteDemoAccountPasswordOpen, setIsDeleteDemoAccountPasswordOpen] =
    useState(false);

  const navigate = useNavigate();

  const {
    deactivateUser,
    deleteUser,
    deleteDemoAccountPassword,
    setDemoAccountPassword,
    updateUserInfo,
    providerUserInfoUpdate,
    cancelActiveScanningPlanForUser,
  } = useAdminUserMutations(userId!);

  const changeTab = (_event: any, newValue: UserDetailTabType) => {
    setActiveTab(newValue);
  };

  const {
    data: user,
    isPending: isGetUserRequestPending,
    isFetching: isGetUserRequestFetching,
    error: getUserError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => UserService.getUserInfoById(userId),
    retry: 3,
    enabled: !!userId,
  });

  const {
    data: allRoles,
    isPending: isAllRolesPending,
    isFetching: isAllRolesFetching,
    error: getAllRolesError,
  } = useQuery({
    queryKey: ['admin-user-all-roles', userId],
    queryFn: () => UserService.loadAllUserRoles(),
    retry: 3,
  });

  const fetchUserDailyData = async ({ startDate, endDate }: DailyDataRange) => {
    const query = queryString.stringify({
      limit: 0,
      user_document_id: user?.document_id,
      day_from: startOfDay(startDate).toISOString(),
      day_to: endOfDay(endDate).toISOString(),
    });

    const paginated = await DailyDataService.loadDailyDataListByQuery(query);

    return paginated.docs;
  };

  const fetchUserCalendarDailyData = async ({
    startDate,
    endDate,
  }: DailyDataRange) => {
    const query = queryString.stringify({
      user_document_id: user?.document_id,
      day_from: startDate.toISOString(),
      day_to: endDate.toISOString(),
      limit: 100,
    });

    const paginated = await DailyDataService.loadDailyDataListByQuery(query);

    return paginated.docs;
  };

  useEffect(() => {
    const setActiveTabFromUrl = () => {
      const pathName = last(location.pathname.split('/')) as UserDetailTabType;
      const validTabs = Object.values(UserDetailTabType);
      if (validTabs.includes(pathName)) {
        setActiveTab(pathName);
      } else {
        setActiveTab(UserDetailTabType.USER_INFO);
      }
    };

    setActiveTabFromUrl();
  }, [location.pathname]);

  const isAccountDeactivated = (): boolean => {
    if (user) {
      const deactivated = '[ACCOUNT DEACTIVATED]';
      const { first_name, last_name, email } = user;

      return (
        first_name === deactivated &&
        last_name === deactivated &&
        email === deactivated
      );
    }

    return false;
  };

  const handleDeactivateUser = () => {
    setOpenConfirmationInputModal(false);
    deactivateUser.mutate();
  };

  const handleDeleteUser = () => {
    setIsOpenDeleteUserDialog(false);
    deleteUser.mutate(undefined, {
      onSuccess: () => navigate(userListPath),
    });
  };

  const handleDeleteDemoAccountPassword = () => {
    setIsDeleteDemoAccountPasswordOpen(false);
    deleteDemoAccountPassword.mutate();
  };

  const handleSetPasswordForDemoAccount = (
    setPasswordAttempt: SetDemoAccountPasswordRequest
  ) => {
    setIsCreatePasswordDialogOpen(false);
    setDemoAccountPassword.mutate(setPasswordAttempt);
  };

  const handleUserDeactivationFirstStepConfirmed = () => {
    setOpenDeactivateConfirmationDialog(false);
    setOpenConfirmationInputModal(true);
  };

  const allErrors = [
    getUserError,
    deactivateUser.error,
    deleteUser.error,
    deleteDemoAccountPassword.error,
    setDemoAccountPassword.error,
    updateUserInfo.error,
    providerUserInfoUpdate.error,
    getAllRolesError,
  ].filter((e): e is Error => !!e);

  const isLoading =
    isGetUserRequestFetching ||
    isGetUserRequestPending ||
    deactivateUser.isPending ||
    deleteUser.isPending ||
    deleteDemoAccountPassword.isPending ||
    setDemoAccountPassword.isPending ||
    updateUserInfo.isPending ||
    providerUserInfoUpdate.isPending ||
    isAllRolesPending ||
    isAllRolesFetching;

  if (!user) {
    return (
      <DetailPageWrapper>
        {getUserError ? (
          <OvRecordDetailsCanvas>
            <OvListStateMessage
              isAlert
              icon={<WarningIcon />}
              title={t('userList.states.detailsErrorTitle')}
              description={getUserError.message}
            >
              <StateActions>
                <OvBackButton
                  label={t('common.users')}
                  fallbackPath={userListPath}
                />
                <OvCompactButton onClick={() => refetchUser()}>
                  {t('common.actions.retry')}
                </OvCompactButton>
              </StateActions>
            </OvListStateMessage>
          </OvRecordDetailsCanvas>
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </DetailPageWrapper>
    );
  }

  const displayName = getUserDisplayName(user);

  return (
    <>
      {
        <DetailPageWrapper aria-busy={isLoading}>
          {allErrors.map((err) => (
            <OvErrorMessage key={err.message} message={err.message} />
          ))}

          <OvRecordDetailsCanvas>
            <OvRecordDetailsHeader
              breadcrumbLabel={t('userList.breadcrumb')}
              backControl={
                <OvBackButton
                  label={t('common.users')}
                  fallbackPath={userListPath}
                />
              }
              current={displayName}
              title={displayName}
              titleAside={
                user.status && (
                  <OvStatusChip
                    label={t(
                      `common.userFields.status.${camelCase(user.status)}`
                    )}
                    tone={getUserStatusTone(user.status)}
                  />
                )
              }
              meta={
                user.email && (
                  <span>
                    {user.email}
                    <OvCopyToClipboardButton
                      text={user.email}
                      label={t('userList.copyEmail')}
                    />
                  </span>
                )
              }
              actions={
                <OvRecordHeaderActions>
                  {!user.is_demo_account && userUpdateAllowed && (
                    <OvCompactButton
                      tone="secondary"
                      onClick={() => setIsResetPasswordDialogOpen(true)}
                    >
                      {t('common.actions.resetPassword')}
                    </OvCompactButton>
                  )}

                  {user.is_demo_account && !user.has_demo_account_password && (
                    <OvCompactButton
                      tone="secondary"
                      onClick={() => setIsCreatePasswordDialogOpen(true)}
                    >
                      {t('common.actions.createDemoAccountPassword')}
                    </OvCompactButton>
                  )}

                  {user.is_demo_account && user.has_demo_account_password && (
                    <OvCompactButton
                      tone="secondary"
                      onClick={() => setIsViewPasswordDialogOpen(true)}
                    >
                      {t('common.actions.viewDemoAccountPassword')}
                    </OvCompactButton>
                  )}

                  {user.is_demo_account && user.has_demo_account_password && (
                    <OvCompactButton
                      tone="secondary"
                      onClick={() => setIsDeleteDemoAccountPasswordOpen(true)}
                    >
                      {t('common.actions.deleteDemoAccountPassword')}
                    </OvCompactButton>
                  )}

                  {!isAccountDeactivated() &&
                    user.document_id &&
                    !user.is_demo_account &&
                    userUpdateAllowed && (
                      <OvCompactButton
                        tone="danger"
                        icon={<PowerSettingsNewIcon />}
                        onClick={() =>
                          setOpenDeactivateConfirmationDialog(true)
                        }
                      >
                        {t('common.actions.deactivate')}
                      </OvCompactButton>
                    )}

                  {!user.document_id && userDeleteAllowed && (
                    <OvCompactButton
                      tone="danger"
                      onClick={() => setIsOpenDeleteUserDialog(true)}
                    >
                      {t('common.actions.delete')}
                    </OvCompactButton>
                  )}
                </OvRecordHeaderActions>
              }
            >
              {user.is_demo_account && (
                <StyledAlert severity="warning">
                  <AlertTitle>{t('userDetails.demoTitle')}</AlertTitle>
                  {t('userDetails.demoDesc')}
                </StyledAlert>
              )}
            </OvRecordDetailsHeader>

            <TabContainer>
              <Tabs onChange={changeTab} value={activeTab} variant="scrollable">
                <Tab
                  value="user-info"
                  label={t('userDetails.tabs.userInfo')}
                  component={Link}
                  to={`${url}/user-info`}
                />
                <Tab
                  value={UserDetailTabType.HORMONE_CHARTS}
                  label={t('dashboard.tabs.hormoneCharts')}
                  component={Link}
                  to={`${url}/${UserDetailTabType.HORMONE_CHARTS}`}
                  replace
                />
                <Tab
                  value={UserDetailTabType.CALENDAR}
                  label={t('userDetails.tabs.calendar')}
                  component={Link}
                  to={`${url}/${UserDetailTabType.CALENDAR}`}
                />

                {pdfReportsReadAllowed && (
                  <Tab
                    value={UserDetailTabType.REPORTS}
                    label={t('userDetails.tabs.reports')}
                    component={Link}
                    to={`${url}/${UserDetailTabType.REPORTS}`}
                  />
                )}
                <Tab
                  value={UserDetailTabType.BASELINES}
                  label={t('userDetails.tabs.baseLines')}
                  component={Link}
                  to={`${url}/${UserDetailTabType.BASELINES}`}
                />
                {scanReadAllowed && (
                  <Tab
                    value={UserDetailTabType.SCANS}
                    label={t('userDetails.tabs.scans')}
                    component={Link}
                    to={`${url}/${UserDetailTabType.SCANS}`}
                  />
                )}
                {userAuditLogActivityReadAllowed && (
                  <Tab
                    value={UserDetailTabType.AUDIT_ACTIVITY}
                    label={t('userDetails.tabs.auditActivity')}
                    component={Link}
                    to={`${url}/${UserDetailTabType.AUDIT_ACTIVITY}`}
                    replace
                  />
                )}
                <Tab
                  value={UserDetailTabType.NOTIFICATIONS}
                  label={t('userDetails.tabs.notifications')}
                  component={Link}
                  to={`${url}/${UserDetailTabType.NOTIFICATIONS}`}
                  replace
                />
                <Tab
                  value={UserDetailTabType.SCANNING_PLAN_HISTORY}
                  label={t('userDetails.tabs.scanningPlanHistory')}
                  component={Link}
                  to={`${url}/${UserDetailTabType.SCANNING_PLAN_HISTORY}`}
                  replace
                />
                <Tab
                  value={UserDetailTabType.SUBSCRIPTIONS}
                  label={t('userDetails.tabs.subscriptions')}
                  component={Link}
                  to={`${url}/${UserDetailTabType.SUBSCRIPTIONS}`}
                  replace
                />
                {hormonePatternReadAllowed && (
                  <Tab
                    value={UserDetailTabType.HORMONE_PATTERN}
                    label={t('userDetails.tabs.hormonePattern')}
                    component={Link}
                    to={`${url}/${UserDetailTabType.HORMONE_PATTERN}`}
                    replace
                  />
                )}
                {strawStageReadAllowed && (
                  <Tab
                    value={UserDetailTabType.REPRODUCTIVE_STAGE}
                    label={t('userDetails.tabs.reproductiveStage')}
                    component={Link}
                    to={`${url}/${UserDetailTabType.REPRODUCTIVE_STAGE}`}
                    replace
                  />
                )}
                {userCycleReadAllowed && (
                  <Tab
                    value={UserDetailTabType.USER_CYCLES}
                    label={t('userDetails.tabs.userCycles')}
                    component={Link}
                    to={`${url}/${UserDetailTabType.USER_CYCLES}`}
                    replace
                  />
                )}
                {ovulatoryEventPredictionReadAllowed && (
                  <Tab
                    value={UserDetailTabType.OVULATORY_EVENT_PREDICTION}
                    label={t('userDetails.tabs.ovulatoryEventPrediction')}
                    component={Link}
                    to={`${url}/${UserDetailTabType.OVULATORY_EVENT_PREDICTION}`}
                    replace
                  />
                )}
              </Tabs>
            </TabContainer>
          </OvRecordDetailsCanvas>

          <Routes>
            <Route
              index
              element={<Navigate to={`${url}/user-info`} replace />}
            />
            <Route
              path="user-info/*"
              element={
                <OvUserInfo
                  selectedUser={user}
                  updateUserInfoMutation={updateUserInfo}
                  updateClinicLocationProviderDataMutation={
                    providerUserInfoUpdate
                  }
                  cancelActiveScanningPlanMutation={
                    cancelActiveScanningPlanForUser
                  }
                  roles={allRoles}
                />
              }
            />
            <Route
              path="hormone-charts/*"
              element={
                <OvUserCharts
                  user={user}
                  fetchDailyData={fetchUserDailyData}
                  dailyDataQueryKey={['user-daily-data', user?.document_id]}
                  fetchCalendarDailyData={fetchUserCalendarDailyData}
                  calendarDailyDataQueryKey={[
                    'user-daily-data-range',
                    user?.document_id,
                  ]}
                  {...(userCycleReadAllowed && {
                    fetchCycles: (query: string) =>
                      getUserCycles(userId, query),
                    fetchCycleHormoneSummary: (cycleId: string) =>
                      getUserCycleHormoneSummary(cycleId),
                    cycleQueryKey: ['admin-user-cycles', userId],
                  })}
                />
              }
            />
            <Route
              path="calendar/*"
              element={<OvUserInfoCalendar selectedUser={user} />}
            />
            <Route
              path="reports/*"
              element={<OvUserDetailReports selectedUser={user} />}
            />
            <Route
              path="baselines/*"
              element={<OvUserSpecificBaselines selectedUser={user} />}
            />
            <Route
              path="scans/*"
              element={<>{user && <UserScanList selectedUser={user} />}</>}
            />
            <Route
              path="audit-activity/*"
              element={<>{user && <OvUserAuditActivity user={user} />}</>}
            />
            <Route
              path="notifications/*"
              element={<>{user && <AdminNotificationList />}</>}
            />
            <Route
              path="scanning-plan-history/*"
              element={
                <>{user && <OvUserScanningPlans selectedUser={user} />}</>
              }
            />
            <Route
              path="subscriptions/*"
              element={<OvUserSubscriptions selectedUser={user} />}
            />
            <Route
              path="hormone-pattern/*"
              element={<OvUserHormonePattern selectedUser={user} />}
            />
            <Route
              path="reproductive-stage/*"
              element={<OvReproductiveStageDetails selectedUser={user} />}
            />
            <Route
              path="user-cycles/*"
              element={<OvUserCycles selectedUser={user} />}
            />
            <Route
              path={`${UserDetailTabType.OVULATORY_EVENT_PREDICTION}/*`}
              element={
                ovulatoryEventPredictionReadAllowed && (
                  <OvOvulatoryEventPrediction
                    selectedUser={user}
                    fetchDailyData={fetchUserDailyData}
                    fetchCalendarDailyData={fetchUserCalendarDailyData}
                  />
                )
              }
            />
          </Routes>
        </DetailPageWrapper>
      }

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isOpenDeactivateConfirmationDialog}
        onCancel={() => setOpenDeactivateConfirmationDialog(false)}
        onConfirm={handleUserDeactivationFirstStepConfirmed}
        title="userDetails.actions.deactivateTitle"
        description="userDetails.actions.deactivateDescription"
      />

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isOpenDeleteUserDialog}
        onCancel={() => setIsOpenDeleteUserDialog(false)}
        onConfirm={handleDeleteUser}
        title="userDetails.actions.deleteTitle"
        description="userDetails.actions.deleteDescription"
      />

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isDeleteDemoAccountPasswordOpen}
        onCancel={() => setIsDeleteDemoAccountPasswordOpen(false)}
        onConfirm={handleDeleteDemoAccountPassword}
        title="userDetails.actions.deleteDemoAccountPasswordTitle"
        description="userDetails.actions.deleteDemoAccountPasswordDescription"
      />

      <OvResetPasswordDialog
        isResetPasswordDialogOpen={isResetPasswordDialogOpen}
        onCancel={() => setIsResetPasswordDialogOpen(false)}
        email={user?.email}
      />

      <OvSetPasswordDialog
        isOpen={isCreatePasswordDialogOpen}
        onCancel={() => setIsCreatePasswordDialogOpen(false)}
        onSave={handleSetPasswordForDemoAccount}
        isLoading={setDemoAccountPassword.isPending}
      />

      <OvViewPasswordDialog
        isOpen={isViewPasswordDialogOpen}
        onCancel={() => setIsViewPasswordDialogOpen(false)}
        userId={user?.id}
      />

      <OvInfoModal
        isOpen={isOpenConfirmationInputModal}
        title={t('common.areYouSure')}
        onCancel={() => setOpenConfirmationInputModal(false)}
        icon={<StyledInfoIcon />}
      >
        <OvConfirmationInput
          expectedValue={user?.email}
          label={t('userDetails.actions.deactivateConfirm')}
          ctaLabel={t('userDetails.actions.deactivateConfirmAction')}
          onMatch={handleDeactivateUser}
          caseSensitive={false}
        />
      </OvInfoModal>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </>
  );
};

export default UserDetails;

const DetailPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.375rem;
  margin-left: 1rem;
  min-width: 0;
  color: ${Colours.OV_BASE};
`;

const StateActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TabContainer = styled.div`
  padding: 0 1rem;

  && {
    .MuiTab-root {
      text-transform: none;
    }

    .MuiTabs-indicator {
      background-color: ${Colours.OV_BASE};
    }
  }
`;

const StyledInfoIcon = styled(InfoIcon)`
  && {
    margin-right: 0.5rem;
  }
`;

const StyledDeleteIcon = styled(PowerSettingsNewIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;

const StyledAlert = styled(Alert)`
  && {
    margin-top: 0.875rem;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
